import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CardContent } from '@/components/ui/card';
import {
  InboxContainer,
  InboxHeader,
  InboxBadge,
  InboxList,
  InboxItemCard,
  InboxItemContent,
  InboxItemIcon,
  InboxItemText,
  InboxItemTitle,
  InboxItemMessage,
  InboxItemTimestamp,
  InboxEmptyState,
  ViewAllLink,
} from '../styles';
import { InboxItem } from '../utils/dashboardTypes';
import { MessageSquareIcon, UserCheckIcon, BellIcon } from '../utils/iconMapping';

interface InboxSectionProps {
  items: InboxItem[];
  limit?: number;
}

/**
 * Icon mapping for different inbox item types
 */
function getIconComponent(iconName: string) {
  const icons: Record<string, any> = {
    MessageSquare: MessageSquareIcon,
    UserCheck: UserCheckIcon,
    Bell: BellIcon,
  };

  return icons[iconName] || BellIcon;
}

/**
 * InboxSection - Displays unread messages, new matches, and action items
 */
export function InboxSection({ items, limit = 3 }: InboxSectionProps) {
  const navigate = useNavigate();

  // Always show limited items (no expansion in horizontal dashboard)
  const displayedItems = items.slice(0, limit);
  const highPriorityCount = items.filter(item => item.priority === 'high').length;

  /**
   * Handles navigation when inbox item is clicked
   */
  const handleItemClick = (item: InboxItem) => {
    navigate(item.link);
  };

  /**
   * Formats timestamp to relative time (e.g., "2 hours ago")
   */
  const formatTimestamp = (timestamp: string) => {
    try {
      const now = new Date();
      const date = new Date(timestamp);
      const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

      if (seconds < 60) return 'just now';
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
      const weeks = Math.floor(days / 7);
      if (weeks < 4) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
      const months = Math.floor(days / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } catch {
      return 'Recently';
    }
  };

  return (
    <InboxContainer>
      <InboxHeader>
        <BellIcon />
        Inbox
        {highPriorityCount > 0 && (
          <InboxBadge aria-label={`${highPriorityCount} high priority items`}>
            {highPriorityCount}
          </InboxBadge>
        )}
      </InboxHeader>

      {items.length === 0 ? (
        <InboxEmptyState>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
          <div style={{ fontWeight: '500' }}>You're all caught up!</div>
          <div style={{ fontSize: '0.8125rem', marginTop: '0.25rem' }}>
            No pending messages or matches to review
          </div>
        </InboxEmptyState>
      ) : (
        <>
          <InboxList>
            {displayedItems.map((item) => {
              const IconComponent = getIconComponent(item.icon);

              return (
                <InboxItemCard
                  key={item.id}
                  priority={item.priority}
                  onClick={() => handleItemClick(item)}
                >
                  <CardContent style={{ padding: '1rem' }}>
                    <InboxItemContent>
                      <InboxItemIcon priority={item.priority}>
                        <IconComponent />
                      </InboxItemIcon>
                      <InboxItemText>
                        <InboxItemTitle>{item.title}</InboxItemTitle>
                        <InboxItemMessage>{item.message}</InboxItemMessage>
                        <InboxItemTimestamp>
                          {formatTimestamp(item.timestamp)}
                        </InboxItemTimestamp>
                      </InboxItemText>
                    </InboxItemContent>
                  </CardContent>
                </InboxItemCard>
              );
            })}
          </InboxList>

          {items.length > limit && (
            <ViewAllLink onClick={() => navigate('/messages')}>
              View All in Messages →
            </ViewAllLink>
          )}
        </>
      )}
    </InboxContainer>
  );
}
